import { ulid } from 'ulid';

import { type AnyDocument, type FullZustandState } from '../../../../types';
import { LogLevel } from '../../../../types/logging';
import { notEmpty } from '../../../../typeValidators';
import makeLogger from '../../../../utils/makeLogger';
// eslint-disable-next-line import/no-cycle
import ttsController from '../../../actions/ttsController.platform';
// eslint-disable-next-line import/no-cycle
import database from '../../../database';
import { CancelStateUpdate, updateState } from '../../../models';
import type { StateUpdateOptions, StateUpdateResult } from '../../../types';
import { setCurrentlyReading } from '../other';
import { updateDocuments } from './update';

const logger = makeLogger(__filename);

const DELETE_CHILDREN_BATCH_SIZE = 100;

export async function deleteDocumentById(
  docId: AnyDocument['id'],
  options: { shouldDeleteChildren?: boolean; } & Parameters<typeof updateState>[1],
): StateUpdateResult {
  const doc = await database.collections.documents.findOne(docId);
  if (!doc) {
    logger.warn('document to delete does not exist', { docId });
    return {
      userEvent: undefined,
    };
  }
  return deleteDocument(doc, options);
}

export async function deleteDocumentsById(
  docIds: AnyDocument['id'][],
  options: { shouldDeleteChildren?: boolean; } & StateUpdateOptions,
): StateUpdateResult {
  options.correlationId ??= ulid();
  if (docIds.length === 0) {
    return {};
  }
  const docs = await database.collections.documents.findByIds(docIds);
  if (docs.length !== docIds.length) {
    logger.error('some documents to delete do not exist', { docIds, docs });
  }
  return deleteDocuments(docs, options);
}

export async function deleteDocuments(
  docsToDelete: AnyDocument[],
  options: { shouldDeleteChildren?: boolean; } & StateUpdateOptions,
): StateUpdateResult {
  if (docsToDelete.length === 0) {
    return {};
  }
  options.correlationId ??= ulid();
  const idsOfDocumentsToDelete = docsToDelete.map(({ id }) => id);
  logger.debug('Deleting multiple documents', {
    docsToDeleteIds: idsOfDocumentsToDelete,
    shouldDeleteChildren: options.shouldDeleteChildren ?? false,
  });
  const idsOfDocumentsToDeleteSet = new Set(idsOfDocumentsToDelete);

  // Collect all children IDs to delete
  const childrenIdsToDelete: string[] = [];
  const docsWithParentToDelete: string[] = [];
  if (options.shouldDeleteChildren) {
    for (const doc of docsToDelete) {
      if (!doc.children) {
        continue;
      }
      childrenIdsToDelete.push(...doc.children);
      docsWithParentToDelete.push(...doc.children);
    }
  }

  const parentDocIds = docsToDelete.map(({ parent }) => parent).filter(notEmpty);
  if (parentDocIds.length > 0) {
    // Ensure we remove these docs from parents first, to avoid data races.
    await updateDocuments(
      parentDocIds,
      (parentDoc) => {
        parentDoc.children = parentDoc.children?.filter((child) => !idsOfDocumentsToDeleteSet.has(child));
      },
      {
        logLevelIfNothingFound: LogLevel.Warn,
        ...options,
      },
    );
  }

  // Delete children in batches to avoid sync issues with large deletions
  if (childrenIdsToDelete.length > 0) {
    logger.debug('Deleting children in batches', {
      totalChildren: childrenIdsToDelete.length,
      batchSize: DELETE_CHILDREN_BATCH_SIZE,
    });

    for (let i = 0; i < childrenIdsToDelete.length; i += DELETE_CHILDREN_BATCH_SIZE) {
      const batch = childrenIdsToDelete.slice(i, i + DELETE_CHILDREN_BATCH_SIZE);
      await database.collections.documents.deleteByIds(batch, options);
    }

    // Also delete any nested children (documents whose parent is being deleted)
    if (docsWithParentToDelete.length > 0) {
      await database.collections.documents.delete(
        {
          selector: {
            parent: { $in: docsWithParentToDelete },
          },
        },
        options,
      );
    }
  }

  // Delete the parent documents themselves
  const [deleteDocsResult] = await Promise.all([
    database.collections.documents.deleteByIds(idsOfDocumentsToDelete, options),
    updateState((state) => {
      if (state.tts?.playingDocId && idsOfDocumentsToDeleteSet.has(state.tts?.playingDocId)) {
        stopTextToSpeech();
      }
      let changed = false;
      for (const doc of docsToDelete) {
        changed ||= removeDocumentFromState(doc, state);
      }
      if (!changed) {
        throw new CancelStateUpdate();
      }
    }, options),
  ]);
  return deleteDocsResult;
}

function stopTextToSpeech() {
  setTimeout(() => {
    ttsController.stop();
    setCurrentlyReading(null, { userInteraction: null });
  }, 50);
}

export async function deleteDocument(
  docToDelete: AnyDocument,
  options: { shouldDeleteChildren?: boolean; } & Parameters<typeof updateState>[1],
): StateUpdateResult {
  logger.debug('Deleting document', {
    docToDelete,
    shouldDeleteChildren: options.shouldDeleteChildren ?? false,
  });
  return deleteDocuments([docToDelete], options);
}

/**
 * @return whether state changed at all.
 */
function removeDocumentFromState(doc: AnyDocument, state: FullZustandState) {
  if (!state.transientDocumentsData[doc.id]) {
    return false;
  }
  delete state.transientDocumentsData[doc.id];
  return true;
}
